import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Clock, Flame, Users, Youtube, Printer, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { KIND_LABEL, pickText, pickList, videoRecipeUrl, fmtMinutes } from "@/lib/recipe";
import LiveFood from "@/components/LiveFood";

export function VideoRecipeLink({ food, code, kind, className = "", testid }) {
  const { lang, t } = useLang();
  const [state, setState] = useState({ loading: false, video: null, open: false });
  const searchUrl = videoRecipeUrl(food, lang);

  const onClick = async (e) => {
    e.preventDefault();
    trackEvent("video_recipe", { country_code: code, kind });
    setState((s) => ({ ...s, loading: true }));
    let video = null;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipes/video/${code}/${kind}`);
      if (r.ok) video = (await r.json()).video;
    } catch { video = null; }
    if (video) setState({ loading: false, video, open: true });
    else { setState({ loading: false, video: null, open: false }); window.open(searchUrl, "_blank", "noopener"); }
  };

  return (
    <>
      <a href={searchUrl} target="_blank" rel="noopener noreferrer" onClick={onClick} title={t("recipe_video_hint")}
        className={`inline-flex items-center gap-1.5 rounded-full bg-[#ff0033]/10 text-[#c4001f] border border-[#ff0033]/25 hover:bg-[#ff0033]/20 transition-colors ${className}`}
        data-testid={testid || `video-recipe-${kind}`}>
        {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Youtube className="w-3.5 h-3.5" />} {t("recipe_video")}
      </a>
      {state.video && (
        <Dialog open={state.open} onOpenChange={(open) => setState((s) => ({ ...s, open }))}>
          <DialogContent className="max-w-3xl rounded-2xl z-[95] p-0 overflow-hidden" onEscapeKeyDown={(e) => e.stopPropagation()} data-testid={`${testid || `video-recipe-${kind}`}-dialog`}>
            <div className="aspect-video bg-black">
              <iframe title={state.video.title} src={`https://www.youtube-nocookie.com/embed/${state.video.video_id}?autoplay=1&rel=0`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="video-iframe" />
            </div>
            <DialogHeader className="text-start px-5 pb-5">
              <DialogTitle className="text-base text-ink leading-snug" data-testid="video-title">{state.video.title}</DialogTitle>
              <DialogDescription className="text-xs flex flex-wrap items-center gap-x-3">
                <span>{state.video.channel}</span>
                <span>· {Math.round(state.video.duration_s / 60)} {t("recipe_min")}</span>
                <a href={searchUrl} target="_blank" rel="noopener noreferrer" className="text-ochre hover:underline" data-testid="video-more-link">{t("recipe_video_more")}</a>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function RecipeMeta({ recipe, size = "text-xs" }) {
  const { t } = useLang();
  const Item = ({ icon: Icon, label, value, testid }) => (
    <span className={`inline-flex items-center gap-1 ${size} text-ink/80`} data-testid={testid}><Icon className="w-3.5 h-3.5 text-ochre" /> {label}{value ? `: ${value}` : ""}</span>
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="recipe-meta">
      <Item icon={Users} label={t("recipe_servings")} testid="recipe-servings" />
      <Item icon={Clock} label={t("recipe_prep")} value={fmtMinutes(recipe.prep_min, t)} testid="recipe-prep" />
      {recipe.cook_min > 0 && <Item icon={Flame} label={t("recipe_cook")} value={fmtMinutes(recipe.cook_min, t)} testid="recipe-cook" />}
    </div>
  );
}

export function RecipeBody({ recipe, lang }) {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.6fr] gap-5">
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-2">{t("guide_ingredients")}</h3>
        <ul className="space-y-1" data-testid="recipe-ingredients">
          {pickList(recipe.ingredients, lang).map((i) => <li key={i} className="text-sm text-ink flex gap-2"><span className="text-ochre">•</span><span>{i}</span></li>)}
        </ul>
      </section>
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-2">{t("recipe_steps")}</h3>
        <ol className="space-y-2.5" data-testid="recipe-steps">
          {pickList(recipe.steps, lang).map((s, i) => (
            <li key={s} className="flex gap-3 text-sm text-ink leading-snug">
              <span className="w-6 h-6 shrink-0 rounded-full bg-ochre text-white text-[11px] font-bold flex items-center justify-center print:bg-ochre">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export default function RecipeDialog({ food, code, kind }) {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const recipe = food.recipe;
  if (!recipe) return null;
  const name = pickText(food.name, lang);
  const onOpen = (v) => { setOpen(v); if (v) trackEvent("view_recipe", { country_code: code, kind }); };
  return (
    <>
      <button type="button" onClick={() => onOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-sand hover:bg-ochre transition-colors" data-testid={`recipe-open-${kind}`}>
        <BookOpen className="w-3.5 h-3.5" /> {t("recipe_view")}
      </button>
      <Dialog open={open} onOpenChange={onOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl z-[90]" onEscapeKeyDown={(e) => e.stopPropagation()} data-testid={`recipe-dialog-${kind}`}>
          <DialogHeader className="text-start">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold">{t("recipe")} · {t(KIND_LABEL[kind])}</div>
            <DialogTitle className="font-display text-2xl text-ink" data-testid="recipe-title">{name}</DialogTitle>
            <DialogDescription className="text-sm">{pickText(food.desc, lang)}</DialogDescription>
          </DialogHeader>
          <LiveFood food={food} kind={kind} alt={name} className="w-full aspect-[16/7] rounded-xl border border-stoneLine" />
          <RecipeMeta recipe={recipe} />
          <RecipeBody recipe={recipe} lang={lang} />
          <div className="flex flex-wrap gap-2 pt-2 border-t border-stoneLine">
            <VideoRecipeLink food={food} code={code} kind={kind} className="text-xs px-3 py-1.5" />
            <Link to={`/recipe/${code}/${kind}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stoneLine hover:bg-sandDeep/50 transition-colors" data-testid={`recipe-print-link-${kind}`}>
              <Printer className="w-3.5 h-3.5" /> {t("recipe_print")}
            </Link>
            <Link to={`/ricettario#${code}-${kind}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stoneLine hover:bg-sandDeep/50 transition-colors" data-testid={`recipe-cookbook-link-${kind}`}>
              <BookOpen className="w-3.5 h-3.5" /> {t("cookbook")}
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
